import React, { Component } from 'react';

import SectionLabel from 'components/controls/sectionLabel';
import CellDetailsSection from './cellDetailsSection';
import CellWorkflowSection from './cellWorkflowSection';
import CellWorkspaceSection from './cellWorkspaceSection';
import { defaultSectionsExpanded, isStructureEditable } from './cellRules';
import { sectionsExpanded, toggleSection } from './sectionsExpanded';

/**
 * CellSections — a cell's three subsections, and which of them are open.
 *
 * Details, Cell Workflow and Workspace always travel together and always in this order, on both
 * surfaces that show a cell: the mission map's dock and the cell's own screen. They were written
 * out twice, which is how the two came to differ before — the screen had no Workspace section at
 * all, and could not edit the other two. One component so that cannot happen again, and so the
 * collapse state does not have to be implemented twice with two chances to disagree.
 *
 * **Collapse is per section and starts from the cell's state** (`defaultSectionsExpanded`): a draft
 * opens everything, anything else opens only Cell Workflow. A person's own toggle beats that
 * default and is kept until the answer changes underneath them — a different cell, or this one
 * leaving draft — which is what `_key` tracks. Without it a collapsed section would stay collapsed
 * across a click onto a cell that ought to open wide, and re-deriving on every render would
 * discard the toggle a person had just made.
 *
 * **Collapsed means something different for Details**, which is why the section is still rendered
 * and told about it rather than skipped: a cell's title is its name, and a section whose header is
 * "Details" is not much of a substitute. So Details keeps its title and drops only the
 * description; the other two go entirely, since neither is a name for anything.
 *
 * Props:
 *   cell, missionId, client — what to render and where to write
 *   templates               — { workflows, workspaces } palettes
 *   onApplyLocal            — (updater) => void; reflect a write without a reload
 *   canEdit                 — false on a committed revision, a closed session, or no write access
 */
class CellSections extends Component {
  state = { key: null, expanded: null };

  // A cell and its editability, which is everything the default depends on. Recomputed on render
  // rather than watched in a lifecycle hook: the state this replaces is a memo, and a memo whose
  // key is derived where it is used cannot go stale.
  _key() {
    const { cell, canEdit } = this.props;
    return `${(cell && cell.element_id) || ''}:${isStructureEditable(cell)}:${canEdit !== false}`;
  }

  _default() {
    return defaultSectionsExpanded(this.props.cell, this.props.canEdit);
  }

  _expanded() {
    return sectionsExpanded(this.state, this._key(), this._default());
  }

  _toggle(section) {
    this.setState(toggleSection(this.state, this._key(), this._default(), section));
  }

  render() {
    const { cell, missionId, client, templates, onApplyLocal, canEdit } = this.props;
    const expanded = this._expanded();
    const section = (name) => ({
      collapsible: true,
      expanded: expanded[name],
      onToggle: () => this._toggle(name),
    });

    return (
      <>
        {/* TITLE and DESCRIPTION are what these fields are called on every surface that touches
            them — these panels, both creation modals, the orchestrator and the seeds — so the
            words a person reads here are the words the rest of the system uses. */}
        <SectionLabel {...section('details')}>Details</SectionLabel>
        {/* Rendered whether or not it is open: collapsed, it is the title alone. */}
        <CellDetailsSection cell={cell} missionId={missionId} client={client}
          onApplyLocal={onApplyLocal} canEdit={canEdit} collapsed={!expanded.details} />

        {/* Cell Workflow — the mode picker while the cell is a draft; what the orchestrator is
            walking once it is not. The section owns that rule, so neither surface can decide it
            differently. */}
        <SectionLabel {...section('workflow')}>Cell Workflow</SectionLabel>
        {expanded.workflow && (
          <CellWorkflowSection cell={cell} missionId={missionId} client={client}
            templates={templates} onApplyLocal={onApplyLocal} canEdit={canEdit} />
        )}

        {/* Workspace — WHERE the agent runs. */}
        <SectionLabel {...section('workspace')}>Workspace</SectionLabel>
        {expanded.workspace && (
          <CellWorkspaceSection cell={cell} missionId={missionId} client={client}
            templates={templates} onApplyLocal={onApplyLocal} canEdit={canEdit} />
        )}
      </>
    );
  }
}

export default CellSections;
